'use client'

import * as React from 'react'

import type { AvatarList } from '@helper/utils/domain'
import cn from '@config/tailwind/cn'

import {
  Avatar as AvatarComponent,
  AvatarFallback,
  AvatarImage,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '../components'
import { typographyVariants } from './typography'

/**
 * Represents a single avatar configuration
 */
export interface OneAvatar {
  /** URL or path to the avatar image */
  src: string
  /** Alt text for the avatar image */
  alt: string
  /** Fallback text to display if image fails to load */
  fallBack?: string
}

/**
 * Avatar component that displays an avatar image with fallback support.
 *
 * @param size - Size of the avatar in pixels
 * @param avatarData - Avatar configuration object
 * @param className - Additional CSS classes
 *
 * @example
 * ```tsx
 * <Avatar
 *   size={60}
 *   avatarData={{
 *     src: '/avatars/user.png',
 *     alt: 'User avatar',
 *     fallBack: 'U'
 *   }}
 * />
 * ```
 */
export function Avatar({ size, avatarData, className }: { size: number; avatarData: OneAvatar; className?: string }) {
  return (
    <AvatarComponent
      className={cn(className)}
      style={{
        width: size,
        height: size,
      }}
    >
      <AvatarImage src={avatarData.src} alt={avatarData.alt} className={cn(className)} width={size} height={size} />
      <AvatarFallback>
        <Skeleton
          className={cn('ui:rounded-full')}
          style={{
            width: size,
            height: size,
          }}
        />
      </AvatarFallback>
    </AvatarComponent>
  )
}

/**
 * Props for the AvatarSelector component
 */
interface AvatarSelectorProps {
  /** Callback function called when an avatar is selected. Can be async. */
  handleAvatarSelectAction: (avatar: AvatarList) => void | Promise<void>
  /** Array of available avatars to choose from */
  avatarData: AvatarList[]
  /** Key of the initially selected avatar */
  initialAvatarKey?: string | null
  /** Fallback text to display when no avatar is selected */
  placeholderFallback?: string
  /** Additional CSS classes for the avatar image */
  className?: string
  /** Additional CSS classes for the trigger button */
  buttonClassName?: string
  /** Additional CSS classes for the popover content */
  popoverClassName?: string
  /** Size of the avatar in pixels (default: 120) */
  size?: number
  /** Whether the selector is disabled */
  disabled?: boolean
  /** Custom title text to display below the avatar */
  customTitle?: string
}

/**
 * A form-friendly avatar selector component with a popover interface.
 *
 * This component allows users to select an avatar from a predefined list,
 * with visual feedback and loading states. It's designed to be easily
 * embedded in forms using TanStack Form or similar form libraries.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AvatarSelector
 *   handleAvatarSelectAction={(avatar) => console.log('Selected:', avatar)}
 *   avatarData={availableAvatars}
 *   initialAvatarKey="avatar-1"
 *   placeholderFallback="?"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Usage in TanStack Form
 * const form = useForm({
 *   defaultValues: {
 *     avatarKey: 'default-avatar',
 *   },
 *   onSubmit: async ({ value }) => {
 *     await saveUserAvatar(value.avatarKey)
 *   },
 * })
 *
 * return (
 *   <form onSubmit={(e) => { e.preventDefault(); form.handleSubmit() }}>
 *     <form.Field name='avatarKey'>
 *       {(field) => (
 *         <AvatarSelector
 *           handleAvatarSelectAction={(avatar) => {
 *             field.handleChange(avatar.key)
 *           }}
 *           avatarData={availableAvatars}
 *           initialAvatarKey={field.state.value}
 *           placeholderFallback="?"
 *           size={100}
 *         />
 *       )}
 *     </form.Field>
 *   </form>
 * )
 * ```
 */
export const AvatarSelector = React.memo(
  ({
    handleAvatarSelectAction,
    avatarData,
    initialAvatarKey,
    placeholderFallback = '',
    className,
    buttonClassName,
    popoverClassName,
    size = 120,
    disabled = false,
    customTitle,
  }: AvatarSelectorProps) => {
    const [selectedAvatar, setSelectedAvatar] = React.useState<AvatarList | null>(null)
    const [isOpen, setIsOpen] = React.useState(false)
    const [isSelecting, setIsSelecting] = React.useState(false)

    const memoizedAvatarData = React.useMemo(() => avatarData, [avatarData])

    // Find initial avatar based on initialAvatarKey
    const initialAvatar = React.useMemo(() => {
      if (!initialAvatarKey) return null
      return memoizedAvatarData.find((avatar) => avatar.key === initialAvatarKey) ?? null
    }, [initialAvatarKey, memoizedAvatarData])

    const handleAvatarSelect = React.useCallback(
      async (avatar: AvatarList) => {
        try {
          setIsSelecting(true)
          setSelectedAvatar(avatar)
          await handleAvatarSelectAction(avatar)
          setIsOpen(false)
        } catch (error) {
          // Reset selection on error
          setSelectedAvatar(selectedAvatar)
          console.error('Error selecting avatar:', error)
        } finally {
          setIsSelecting(false)
        }
      },
      [handleAvatarSelectAction, selectedAvatar],
    )

    const currentAvatar = React.useMemo(() => {
      return selectedAvatar ?? initialAvatar
    }, [selectedAvatar, initialAvatar])

    const buttonText = React.useMemo(() => {
      if (isSelecting) return 'Memilih...'
      if (customTitle) return customTitle
      if (currentAvatar) return 'Ganti avatar'
      return 'Pilih avatar'
    }, [isSelecting, customTitle, currentAvatar])

    const displayAvatar: OneAvatar = React.useMemo(() => {
      if (currentAvatar) {
        return {
          src: currentAvatar.src,
          alt: currentAvatar.alt,
          fallBack: currentAvatar.alt[0] ?? placeholderFallback,
        }
      }
      // Return a placeholder avatar when none is selected
      return {
        src: '',
        alt: 'Avatar placeholder',
        fallBack: placeholderFallback,
      }
    }, [currentAvatar, placeholderFallback])

    return (
      <Popover
        open={isOpen}
        onOpenChange={(newOpen) => {
          if (!isSelecting && !disabled) {
            setIsOpen(newOpen)
          }
        }}
      >
        <PopoverTrigger
          render={(props) => (
            <Button
              onClick={() => setIsOpen(true)}
              baseButton={disabled}
              variant='ghost'
              className={cn(
                'ui:flex ui:flex-col ui:items-center ui:justify-center ui:gap-2 ui:w-fit ui:h-fit ui:p-0',
                buttonClassName,
              )}
              style={{
                width: size + 45,
                minHeight: size + 50,
                cursor: disabled || isSelecting ? 'default' : 'pointer',
              }}
              disabled={disabled || isSelecting}
              aria-label={`${buttonText}`}
              {...props}
            >
              <div className='ui:relative'>
                <Avatar size={size} avatarData={displayAvatar} className={cn(className)} />
              </div>
              <span
                className={cn(
                  typographyVariants({ variant: 'label-md' }),
                  'ui:text-blue-100',
                  isSelecting && 'ui:opacity-50',
                )}
              >
                {buttonText}
              </span>
            </Button>
          )}
        />

        <PopoverContent className={cn('ui:w-[280px]', popoverClassName)}>
          <div className='ui:grid ui:gap-4'>
            <h4 className={cn(typographyVariants({ variant: 'label-md' }))}>Pilih avatar</h4>
            <div className='ui:grid ui:grid-cols-4 ui:gap-2'>
              {memoizedAvatarData.map((avatar) => {
                const isCurrentlySelected = currentAvatar?.key === avatar.key
                return (
                  <Button
                    key={avatar.key}
                    variant='ghost'
                    className={cn(
                      'ui:p-0 ui:w-[50px] ui:h-[50px] ui:rounded-full ui:transition-all',
                      isCurrentlySelected && 'ui:ring-2 ui:ring-blue-100',
                    )}
                    onClick={() => handleAvatarSelect(avatar)}
                    disabled={isSelecting}
                    aria-label={`Pilih ${avatar.alt}`}
                  >
                    <Avatar
                      size={50}
                      avatarData={{ src: avatar.src, alt: avatar.alt, fallBack: avatar.alt[0] ?? '' }}
                    />
                  </Button>
                )
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    )
  },
)

AvatarSelector.displayName = 'AvatarSelector'
